// src/components/CountryPageClient.tsx
"use client";

import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslations } from "@/context/TranslationsContext";
import { useCurrency } from "@/context/CurrencyContext";
import { getCountryName, registerCountryLocale } from "@/lib/countryTranslations";
import { BundleWithCountries, Country } from "@/types/types";
import { formatBundleName } from "@/utils/bundleUtils";
import {
  convertPrice,
  formatCurrency,
  isStripeCurrency,
} from "@/utils/currencyUtils";
import { getExchangeRate } from "@/utils/exchangeRateCache";
import logger from "@/utils/logger.client";
import CountryContent from "./CountryContent";

// Dynamically load the grid component (no SSR)
const CountryBundlesGrid = dynamic(
  () => import("./CountryBundlesGrid"),
  {
    ssr: false,
    loading: () => (
      <p className="text-center py-8 text-gray-500">
        Loading bundles…
      </p>
    ),
  }
);

const Modal = dynamic(() => import("@/components/Modal"), {
  ssr: false,
  loading: () => null,
});

const rtlLanguages = ["ar", "he", "fa", "ur"];

interface CountryPageClientProps {
  country: Country & { networkBrands: string[] };
  bundles: BundleWithCountries[];
  allCountries: { iso: string; name: string }[];
  currencyCode: string;
  exchangeRate: number;
}

const CountryPageClient: React.FC<CountryPageClientProps> = ({
  country,
  bundles,
  allCountries,
  currencyCode,
  exchangeRate,
}) => {
  const { t, language } = useTranslations();
  const isRtl = rtlLanguages.includes(language);

  registerCountryLocale(language);

  if (!isStripeCurrency(currencyCode) || exchangeRate <= 0) {
    logger.error("CountryPageClient: Invalid currency context detected", {
      currencyCode,
      exchangeRate,
      countryIso: country.iso,
    });
    // Throwing to fail-fast in React render
    throw new Error("Invalid currency context");
  }

  // Verify exchange rate on mount
  useEffect(() => {
    (async () => {
      try {
        const rate = await getExchangeRate(currencyCode);
        if (Math.abs(rate - exchangeRate) / rate > 0.01) {
          logger.warn(
            "CountryPageClient: Client exchange rate mismatch detected. Consider updating context.",
            { currencyCode, clientRate: exchangeRate, freshRate: rate }
          );
        }
      } catch (err: any) {
        logger.error("CountryPageClient: Error verifying exchange rate", {
          error: err.message ?? err,
          currencyCode,
        });
      }
    })();
  }, [currencyCode, exchangeRate]);

  // Build a map of bundle.id -> localized bundle name
  const [bundleNames, setBundleNames] = useState<Record<string, string>>({});
  useEffect(() => {
    (async () => {
      try {
        const baseName = country.name;
        const pairs = await Promise.all(
          bundles.map(async (b) => {
            const formatted = await formatBundleName(b, language);
            return [b.id.toString(), `${formatted} ${baseName}`] as const;
          })
        );
        setBundleNames(Object.fromEntries(pairs));
      } catch (err: any) {
        logger.error("CountryPageClient: Failed to format bundle names", {
          error: err.message ?? err,
          country: country.iso,
        });
      }
    })();
  }, [bundles, country.name, language]);

  // Modal state
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<BundleWithCountries | null>(null);
  const handleBuy = useCallback((b: BundleWithCountries) => {
    setSelected(b);
    setOpen(true);
  }, []);

  return (
    <>
      {/* 1) Defer rendering of the heavy bundle grid with reserved space */}
      <div className="min-h-[300px]">
        <CountryBundlesGrid
          country={country}
          bundles={bundles.map((b) => ({
            ...b,
            // attach localized name so child can pick it up
            localizedName: bundleNames[b.id.toString()] || "",
          }))}
          allCountries={allCountries}
          currencyCode={currencyCode}
          exchangeRate={exchangeRate}
          onBuy={handleBuy}
        />
      </div>

      {/* 2) Render remaining content */}
      <CountryContent
        countryName={country.name}
        networks={country.networkBrands}
      />

      {open && selected && (
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          country={country}
          initialBundle={selected}
          currencyCode={currencyCode}
          exchangeRate={exchangeRate}
        />
      )}
    </>
  );
};

export default React.memo(CountryPageClient);
